import { useProjectStore } from '../../store/projectStore';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { TextInput, TextArea, Select } from '../ui/FormField';
import { ColorPicker } from '../ui/ColorPicker';
import { ImageUploader } from '../ui/ImageUploader';
import { DynamicList } from '../ui/DynamicList';
import { ThemeToggle } from '../ui/ThemeToggle';
import type {
  ProjectConfig,
  BrandConfig,
  ContactConfig,
  SocialConfig,
  DesignConfig,
  PagesConfig,
  HeroConfig,
  ServiceItem,
  TeamMember,
  PropertyItem,
  NewsItem,
  TechConfig,
  DeployConfig,
} from '../../types';
import { generateId } from '../../utils/helpers';
import { validateEmail, validatePhone, validateUrl } from '../../lib/validators';

function updateNested<T extends Record<string, unknown>>(
  parent: T,
  updates: Partial<T>
): T {
  return { ...parent, ...updates };
}

export function ProjectConfig() {
  const config = useProjectStore((state) => state.config);
  const setConfig = useProjectStore((state) => state.setConfig);

  const showPropertyFields = config.archetype === 'cms' || config.industry === 'property';

  const updateBrand = (updates: Partial<BrandConfig>) => {
    setConfig({ brand: updateNested(config.brand, updates) });
  };

  const updateContact = (updates: Partial<ContactConfig>) => {
    setConfig({ contact: updateNested(config.contact, updates) });
  };

  const updateSocial = (updates: Partial<SocialConfig>) => {
    setConfig({ social: updateNested(config.social, updates) });
  };

  const updateDesign = (updates: Partial<DesignConfig>) => {
    setConfig({ design: updateNested(config.design, updates) });
  };

  const updatePages = (updates: Partial<PagesConfig>) => {
    setConfig({ pages: updateNested(config.pages, updates) });
  };

  const updateHero = (updates: Partial<HeroConfig>) => {
    setConfig({ hero: updateNested(config.hero, updates) });
  };

  const updateTech = (updates: Partial<TechConfig>) => {
    setConfig({ tech: updateNested(config.tech, updates) });
  };

  const updateDeploy = (updates: Partial<DeployConfig>) => {
    setConfig({ deploy: updateNested(config.deploy, updates) });
  };

  const emailError = config.contact.email && !validateEmail(config.contact.email) ? '請輸入有效的電郵地址' : undefined;
  const phoneError = config.contact.phone && !validatePhone(config.contact.phone) ? '請輸入有效的電話號碼' : undefined;

  return (
    <div className="max-w-4xl mx-auto pb-10">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">項目配置</h2>
        <ThemeToggle />
      </div>

      <CollapsibleSection title="基本資訊" defaultOpen>
        <TextInput
          label="專案名稱（英文，用於文件名）"
          value={config.name}
          onChange={(e) => setConfig({ name: e.target.value })}
          required
        />
        <TextInput
          label="顯示名稱"
          value={config.displayName}
          onChange={(e) => setConfig({ displayName: e.target.value })}
          required
        />
        <Select
          label="專案類型"
          value={config.projectType}
          onChange={(e) => setConfig({ projectType: e.target.value as ProjectConfig['projectType'] })}
          options={[
            { value: 'cms', label: 'CMS 內容管理系統' },
            { value: 'saas', label: 'SaaS' },
            { value: 'landing', label: 'Landing Page' },
            { value: 'blog', label: '部落格' },
            { value: 'ecommerce', label: '電商' },
          ]}
        />
        <Select
          label="語言"
          value={config.language}
          onChange={(e) => setConfig({ language: e.target.value as ProjectConfig['language'] })}
          options={[
            { value: 'zh-Hant', label: '繁體中文' },
            { value: 'zh-Hans', label: '簡體中文' },
            { value: 'en', label: '英文' },
          ]}
        />
        <TextInput
          label="版本"
          value={config.version}
          onChange={(e) => setConfig({ version: e.target.value })}
        />
      </CollapsibleSection>

      <CollapsibleSection title="品牌資料" defaultOpen>
        <TextInput
          label="公司名稱（中文）"
          value={config.brand.nameCn}
          onChange={(e) => updateBrand({ nameCn: e.target.value })}
          required
        />
        <TextInput
          label="公司名稱（英文）"
          value={config.brand.nameEn}
          onChange={(e) => updateBrand({ nameEn: e.target.value })}
          required
        />
        <TextInput
          label="標語"
          value={config.brand.tagline}
          onChange={(e) => updateBrand({ tagline: e.target.value })}
        />
        <TextArea
          label="公司簡介"
          value={config.brand.description}
          onChange={(e) => updateBrand({ description: e.target.value })}
          rows={4}
        />
        <ImageUploader
          label="Logo 圖片"
          value={config.brand.logoUrl}
          onChange={(value) => updateBrand({ logoUrl: value })}
        />
        <TextInput
          label="成立年份"
          value={config.brand.foundedYear}
          onChange={(e) => updateBrand({ foundedYear: e.target.value })}
        />
        <TextInput
          label="牌照號碼"
          value={config.brand.licenseNumber}
          onChange={(e) => updateBrand({ licenseNumber: e.target.value })}
        />
      </CollapsibleSection>

      <CollapsibleSection title="聯絡資訊">
        <TextInput
          label="電話"
          value={config.contact.phone}
          onChange={(e) => updateContact({ phone: e.target.value })}
          error={phoneError}
          required
        />
        <TextInput
          label="電話顯示格式"
          value={config.contact.phoneDisplay}
          onChange={(e) => updateContact({ phoneDisplay: e.target.value })}
        />
        <TextInput
          label="電郵"
          value={config.contact.email}
          onChange={(e) => updateContact({ email: e.target.value })}
          error={emailError}
          required
        />
        <TextArea
          label="地址"
          value={config.contact.address}
          onChange={(e) => updateContact({ address: e.target.value })}
          rows={2}
        />
        <TextInput
          label="地址顯示格式"
          value={config.contact.addressDisplay}
          onChange={(e) => updateContact({ addressDisplay: e.target.value })}
        />
        <TextInput
          label="營業時間"
          value={config.contact.businessHours}
          onChange={(e) => updateContact({ businessHours: e.target.value })}
        />
        <TextInput
          label="Google Maps 連結"
          value={config.contact.mapLink}
          onChange={(e) => updateContact({ mapLink: e.target.value })}
          error={config.contact.mapLink && !validateUrl(config.contact.mapLink) ? '請輸入有效的 URL' : undefined}
        />
        <DynamicList
          label="WhatsApp 號碼"
          items={config.contact.whatsapp}
          onChange={(items) => updateContact({ whatsapp: items })}
          onAdd={() => updateContact({ whatsapp: [...config.contact.whatsapp, ''] })}
          emptyText="尚未新增 WhatsApp 號碼"
          renderItem={(item, _index, onChange) => (
            <TextInput
              label=""
              value={item}
              onChange={(e) => onChange(e.target.value)}
              placeholder="+85212345678"
            />
          )}
        />
      </CollapsibleSection>

      <CollapsibleSection title="社交連結">
        <TextInput
          label="Facebook"
          value={config.social.facebook || ''}
          onChange={(e) => updateSocial({ facebook: e.target.value })}
          error={config.social.facebook && !validateUrl(config.social.facebook) ? '請輸入有效的 URL' : undefined}
        />
        <TextInput
          label="Instagram"
          value={config.social.instagram || ''}
          onChange={(e) => updateSocial({ instagram: e.target.value })}
          error={config.social.instagram && !validateUrl(config.social.instagram) ? '請輸入有效的 URL' : undefined}
        />
        <TextInput
          label="WhatsApp 連結"
          value={config.social.whatsapp || ''}
          onChange={(e) => updateSocial({ whatsapp: e.target.value })}
        />
        <TextInput
          label="WeChat"
          value={config.social.wechat || ''}
          onChange={(e) => updateSocial({ wechat: e.target.value })}
        />
        <TextInput
          label="LinkedIn"
          value={config.social.linkedin || ''}
          onChange={(e) => updateSocial({ linkedin: e.target.value })}
          error={config.social.linkedin && !validateUrl(config.social.linkedin) ? '請輸入有效的 URL' : undefined}
        />
        <TextInput
          label="YouTube"
          value={config.social.youtube || ''}
          onChange={(e) => updateSocial({ youtube: e.target.value })}
          error={config.social.youtube && !validateUrl(config.social.youtube) ? '請輸入有效的 URL' : undefined}
        />
      </CollapsibleSection>

      <CollapsibleSection title="視覺設計" defaultOpen>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ColorPicker
            label="主色"
            value={config.design.primaryColor}
            onChange={(value) => updateDesign({ primaryColor: value })}
            required
          />
          <ColorPicker
            label="輔色"
            value={config.design.secondaryColor}
            onChange={(value) => updateDesign({ secondaryColor: value })}
            required
          />
          <ColorPicker
            label="強調色"
            value={config.design.accentColor}
            onChange={(value) => updateDesign({ accentColor: value })}
            required
          />
          <ColorPicker
            label="背景色"
            value={config.design.backgroundColor}
            onChange={(value) => updateDesign({ backgroundColor: value })}
            required
          />
          <ColorPicker
            label="文字色"
            value={config.design.textColor}
            onChange={(value) => updateDesign({ textColor: value })}
            required
          />
        </div>
        <Select
          label="字體"
          value={config.design.fontFamily}
          onChange={(e) => updateDesign({ fontFamily: e.target.value as DesignConfig['fontFamily'] })}
          options={[
            { value: 'system', label: '系統預設' },
            { value: 'inter', label: 'Inter' },
            { value: 'noto-sans-tc', label: 'Noto Sans TC' },
            { value: 'noto-sans-sc', label: 'Noto Sans SC' },
          ]}
        />
        <TextInput
          label="圓角（px）"
          type="number"
          value={String(config.design.borderRadius)}
          onChange={(e) => updateDesign({ borderRadius: Number(e.target.value) })}
        />
        <Select
          label="陰影強度"
          value={config.design.shadowIntensity}
          onChange={(e) => updateDesign({ shadowIntensity: e.target.value as DesignConfig['shadowIntensity'] })}
          options={[
            { value: 'none', label: '無' },
            { value: 'light', label: '輕' },
            { value: 'medium', label: '中' },
            { value: 'heavy', label: '重' },
          ]}
        />
      </CollapsibleSection>

      <CollapsibleSection title="頁面結構">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(Object.keys(config.pages) as Array<keyof PagesConfig>).map((key) => (
            <label key={key} className="flex items-center gap-2 p-2 rounded border border-slate-200 dark:border-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={config.pages[key]}
                onChange={(e) => updatePages({ [key]: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm capitalize">{key}</span>
            </label>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="首頁 Hero">
        <TextInput
          label="小標題"
          value={config.hero.tagline}
          onChange={(e) => updateHero({ tagline: e.target.value })}
        />
        <TextInput
          label="主標題第一行"
          value={config.hero.titleLine1}
          onChange={(e) => updateHero({ titleLine1: e.target.value })}
          required
        />
        <TextInput
          label="主標題第二行"
          value={config.hero.titleLine2}
          onChange={(e) => updateHero({ titleLine2: e.target.value })}
        />
        <TextArea
          label="描述"
          value={config.hero.description}
          onChange={(e) => updateHero({ description: e.target.value })}
          rows={3}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInput
            label="主要按鈕文字"
            value={config.hero.primaryButtonText}
            onChange={(e) => updateHero({ primaryButtonText: e.target.value })}
          />
          <TextInput
            label="主要按鈕連結"
            value={config.hero.primaryButtonLink}
            onChange={(e) => updateHero({ primaryButtonLink: e.target.value })}
          />
          <TextInput
            label="次要按鈕文字"
            value={config.hero.secondaryButtonText}
            onChange={(e) => updateHero({ secondaryButtonText: e.target.value })}
          />
          <TextInput
            label="次要按鈕連結"
            value={config.hero.secondaryButtonLink}
            onChange={(e) => updateHero({ secondaryButtonLink: e.target.value })}
          />
        </div>
        <Select
          label="背景類型"
          value={config.hero.backgroundType}
          onChange={(e) => updateHero({ backgroundType: e.target.value as HeroConfig['backgroundType'] })}
          options={[
            { value: 'gradient', label: '漸層' },
            { value: 'image', label: '圖片' },
            { value: 'video', label: '影片' },
            { value: 'solid', label: '純色' },
          ]}
        />
        <TextInput
          label="背景值（圖片 URL 或顏色）"
          value={config.hero.backgroundValue || ''}
          onChange={(e) => updateHero({ backgroundValue: e.target.value })}
        />
      </CollapsibleSection>

      <CollapsibleSection title="服務項目">
        <DynamicList
          label=""
          items={config.services}
          onChange={(items) => setConfig({ services: items })}
          onAdd={() =>
            setConfig({
              services: [
                ...config.services,
                { id: generateId(), icon: 'Star', title: '新服務', description: '' },
              ],
            })
          }
          emptyText="尚未新增服務"
          renderItem={(item, _index, onChange) => (
            <div className="space-y-2">
              <TextInput
                label=""
                value={item.title}
                onChange={(e) => onChange({ ...item, title: e.target.value })}
                placeholder="服務名稱"
              />
              <TextInput
                label=""
                value={item.icon}
                onChange={(e) => onChange({ ...item, icon: e.target.value })}
                placeholder="Lucide 圖標名稱"
              />
              <TextArea
                label=""
                value={item.description}
                onChange={(e) => onChange({ ...item, description: e.target.value })}
                placeholder="服務描述"
                rows={2}
              />
            </div>
          )}
        />
      </CollapsibleSection>

      <CollapsibleSection title="內容區塊">
        <DynamicList
          label=""
          items={config.contentBlocks}
          onChange={(items) => setConfig({ contentBlocks: items })}
          onAdd={() =>
            setConfig({
              contentBlocks: [
                ...config.contentBlocks,
                { id: generateId(), type: 'text', title: '新區塊', content: '' },
              ],
            })
          }
          emptyText="尚未新增內容區塊"
          renderItem={(item, _index, onChange) => (
            <div className="space-y-2">
              <TextInput
                label=""
                value={item.title}
                onChange={(e) => onChange({ ...item, title: e.target.value })}
                placeholder="區塊標題"
              />
              <Select
                label=""
                value={item.type}
                onChange={(e) => onChange({ ...item, type: e.target.value })}
                options={[
                  { value: 'text', label: '文字' },
                  { value: 'image', label: '圖片' },
                  { value: 'quote', label: '引用' },
                  { value: 'cta', label: 'CTA' },
                  { value: 'feature', label: '特色' },
                  { value: 'custom', label: '自訂' },
                ]}
              />
              <TextArea
                label=""
                value={item.content}
                onChange={(e) => onChange({ ...item, content: e.target.value })}
                placeholder="內容"
                rows={3}
              />
            </div>
          )}
        />
      </CollapsibleSection>

      <CollapsibleSection title="銷售團隊">
        <DynamicList
          label=""
          items={config.team}
          onChange={(items) => setConfig({ team: items })}
          onAdd={() =>
            setConfig({
              team: [...config.team, { id: generateId(), name: '', title: '', whatsapp: '', email: '', photo: '' }],
            })
          }
          emptyText="尚未新增團隊成員"
          renderItem={(item, _index, onChange) => (
            <div className="space-y-2">
              <TextInput
                label=""
                value={item.name}
                onChange={(e) => onChange({ ...item, name: e.target.value })}
                placeholder="姓名"
              />
              <TextInput
                label=""
                value={item.title}
                onChange={(e) => onChange({ ...item, title: e.target.value })}
                placeholder="職稱"
              />
              <TextInput
                label=""
                value={item.whatsapp}
                onChange={(e) => onChange({ ...item, whatsapp: e.target.value })}
                placeholder="WhatsApp"
              />
              <TextInput
                label=""
                value={item.email || ''}
                onChange={(e) => onChange({ ...item, email: e.target.value })}
                placeholder="電郵"
              />
            </div>
          )}
        />
      </CollapsibleSection>

      {showPropertyFields && (
        <>
          <CollapsibleSection title="物業資料">
            <DynamicList
              label=""
              items={config.properties ?? []}
              onChange={(items) => setConfig({ properties: items })}
              onAdd={() => {
                const currentProperties = config.properties ?? [];
                setConfig({
                  properties: [
                    ...currentProperties,
                    {
                      id: generateId(),
                      code: `MW-${String(currentProperties.length + 1).padStart(3, '0')}`,
                      title: '',
                      category: '辦公室',
                      price: 0,
                      priceUnit: '售',
                      area: 0,
                      areaUnit: '平方呎',
                      description: '',
                      images: [],
                      featured: false,
                    },
                  ],
                });
              }}
              emptyText="尚未新增物業"
              renderItem={(item, _index, onChange) => (
                <div className="space-y-2">
                  <TextInput
                    label=""
                    value={item.title}
                    onChange={(e) => onChange({ ...item, title: e.target.value })}
                    placeholder="標題"
                  />
                  <TextInput
                    label=""
                    value={item.category}
                    onChange={(e) => onChange({ ...item, category: e.target.value })}
                    placeholder="類型"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <TextInput
                      label=""
                      type="number"
                      value={String(item.price)}
                      onChange={(e) => onChange({ ...item, price: Number(e.target.value) })}
                      placeholder="價格"
                    />
                    <TextInput
                      label=""
                      value={item.priceUnit}
                      onChange={(e) => onChange({ ...item, priceUnit: e.target.value })}
                      placeholder="單位"
                    />
                  </div>
                  <TextArea
                    label=""
                    value={item.description}
                    onChange={(e) => onChange({ ...item, description: e.target.value })}
                    placeholder="描述"
                    rows={2}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={item.featured}
                      onChange={(e) => onChange({ ...item, featured: e.target.checked })}
                      className="rounded"
                    />
                    精選物業
                  </label>
                </div>
              )}
            />
          </CollapsibleSection>

          <CollapsibleSection title="最新消息">
            <DynamicList
              label=""
              items={config.news ?? []}
              onChange={(items) => setConfig({ news: items })}
              onAdd={() => {
                const currentNews = config.news ?? [];
                setConfig({
                  news: [
                    ...currentNews,
                    {
                      id: generateId(),
                      title: '',
                      date: new Date().toISOString().split('T')[0],
                      summary: '',
                      content: '',
                      image: '',
                      category: '一般',
                    },
                  ],
                });
              }}
              emptyText="尚未新增消息"
              renderItem={(item, _index, onChange) => (
                <div className="space-y-2">
                  <TextInput
                    label=""
                    value={item.title}
                    onChange={(e) => onChange({ ...item, title: e.target.value })}
                    placeholder="標題"
                  />
                  <TextInput
                    label=""
                    type="date"
                    value={item.date}
                    onChange={(e) => onChange({ ...item, date: e.target.value })}
                  />
                  <TextInput
                    label=""
                    value={item.category}
                    onChange={(e) => onChange({ ...item, category: e.target.value })}
                    placeholder="分類"
                  />
                  <TextArea
                    label=""
                    value={item.summary}
                    onChange={(e) => onChange({ ...item, summary: e.target.value })}
                    placeholder="摘要"
                    rows={2}
                  />
                </div>
              )}
            />
          </CollapsibleSection>
        </>
      )}

      <CollapsibleSection title="技術設定">
        <Select
          label="前端框架"
          value={config.tech.frontend}
          onChange={(e) => updateTech({ frontend: e.target.value as TechConfig['frontend'] })}
          options={[{ value: 'react-vite-tailwind', label: 'React + Vite + Tailwind' }]}
        />
        <Select
          label="後端框架"
          value={config.tech.backend}
          onChange={(e) => updateTech({ backend: e.target.value as TechConfig['backend'] })}
          options={[{ value: 'cloudflare-worker-hono', label: 'Cloudflare Worker + Hono' }]}
        />
        <Select
          label="儲存方案"
          value={config.tech.storage}
          onChange={(e) => updateTech({ storage: e.target.value as TechConfig['storage'] })}
          options={[
            { value: 'kv', label: 'Cloudflare KV' },
            { value: 'd1', label: 'Cloudflare D1' },
            { value: 'kv+r2', label: 'KV + R2' },
          ]}
        />
        <Select
          label="認證方式"
          value={config.tech.auth}
          onChange={(e) => updateTech({ auth: e.target.value as TechConfig['auth'] })}
          options={[
            { value: 'none', label: '無' },
            { value: 'basic', label: 'Basic Auth' },
            { value: 'jwt', label: 'JWT' },
          ]}
        />
        <label className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            checked={config.tech.analytics}
            onChange={(e) => updateTech({ analytics: e.target.checked })}
            className="w-4 h-4 rounded text-blue-600"
          />
          <span className="text-sm">啟用分析</span>
        </label>
      </CollapsibleSection>

      <CollapsibleSection title="部署設定">
        <TextInput
          label="Worker 子網域"
          value={config.deploy.workerSubdomain}
          onChange={(e) => updateDeploy({ workerSubdomain: e.target.value })}
          required
        />
        <TextInput
          label="Pages 專案名稱"
          value={config.deploy.pagesProjectName}
          onChange={(e) => updateDeploy({ pagesProjectName: e.target.value })}
          required
        />
        <TextInput
          label="自訂域名（可選）"
          value={config.deploy.customDomain || ''}
          onChange={(e) => updateDeploy({ customDomain: e.target.value })}
        />
        <TextInput
          label="Cloudflare Account ID（可選）"
          value={config.deploy.cloudflareAccountId || ''}
          onChange={(e) => updateDeploy({ cloudflareAccountId: e.target.value })}
        />
      </CollapsibleSection>

      <CollapsibleSection title="自定義變數">
        <CustomVarsEditor value={config.customVars} onChange={(customVars) => setConfig({ customVars })} />
      </CollapsibleSection>
    </div>
  );
}

function CustomVarsEditor({
  value,
  onChange,
}: {
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
}) {
  const entries = Object.entries(value);

  return (
    <div className="space-y-2">
      {entries.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">尚未新增自定義變數</p>
      )}
      {entries.map(([key, val], index) => (
        <div key={index} className="flex gap-2">
          <input
            type="text"
            value={key}
            onChange={(e) => {
              const next: Record<string, string> = {};
              entries.forEach(([k, v], i) => {
                next[i === index ? e.target.value : k] = v;
              });
              onChange(next);
            }}
            placeholder="KEY"
            className="flex-1 px-3 py-2 rounded border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
          />
          <input
            type="text"
            value={val}
            onChange={(e) => {
              const next = { ...value, [key]: e.target.value };
              onChange(next);
            }}
            placeholder="value"
            className="flex-1 px-3 py-2 rounded border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
          />
          <button
            type="button"
            onClick={() => {
              const next = { ...value };
              delete next[key];
              onChange(next);
            }}
            className="px-3 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
          >
            刪除
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange({ ...value, [`CUSTOM_${entries.length + 1}`]: '' })}
        className="px-4 py-2 text-sm rounded text-white"
        style={{ background: 'var(--pf-primary, #3b82f6)' }}
      >
        新增變數
      </button>
    </div>
  );
}
